<template>
  <div class="card-body p-2">
    <div class="">
      <div header-tag="header" role="tab">
        <div class="d-flex justify-content-between">
          <div class="card d-flex flex-column gap-1 m-0">
            <div class="d-flex align-items-center gap-2">
              <div
                class="rounded-circle bg-light d-flex align-items-center justify-content-center icon-container"
              >
                <i class="mc-fa icon-fill_cube text-primary"></i>
              </div>
              <div class="d-flex flex-column">
                <div class="d-flex align-items-center gap-2">
                  <h5 class="mb-0">
                    {{ $t('fleetTask.deliveryLoads.title') }}
                  </h5>
                  <McBadge
                    :icon="badgeIcon"
                    :iconVariant="badgeVariant"
                    :text="badgeText"
                    :variant="badgeOutline"
                    size="medium"
                    type="button"
                  />
                </div>
                <small class="text-muted">
                  {{ $t('fleetTask.deliveryLoads.subtitle') }}
                </small>
              </div>
            </div>
          </div>
          <p
            v-if="
              isAdjustedActivity &&
              ((isCompletedPickUp && isPickUp) || (isCompletedPickUp && isCompletedDrop))
            "
            class="text-muted mb-0 timestamp-text"
          >
            <span>
              <i class="icon-fill_sync"></i>
            </span>
            {{ $t('fleetTask.deliveryLoads.lastUpdated', { value: lastUpdateFormatted }) }}
          </p>
        </div>
      </div>
      <div v-if="!isComplete" role="tabpanel">
        <div v-if="!isSingleComplete" class="d-flex justify-content-end mb-2">
          <b-form-input
            ref="searchInput"
            v-model="searchQuery"
            class="w-25"
            :placeholder="$t('global.search') + '...'"
          />
        </div>
        <div
          v-if="isInitialPrefetching && !isSingleComplete"
          class="d-flex flex-column align-items-center justify-content-center text-muted py-4"
        >
          <div class="d-flex flex-column gap-2 w-100">
            <b-skeleton
              v-for="index in filteredActivities.length"
              :key="`activity-skeleton-${index}`"
              animation="wave"
              width="100%"
            />
          </div>
        </div>
        <div v-else-if="showSearchEmptyState" class="card-body card-body-collapse p-2 mb-2">
          <ValueHelpTable
            class="delivery-loads-table"
            :isBusy="false"
            :attrs="tableAttrs"
            :listGetter="emptyListGetter"
            :localItems="[]"
            :multiple="false"
            :selectable="false"
            :totalCount="0"
          />
        </div>
        <template v-for="(act, idx) in filteredActivities" v-else>
          <div
            :key="act?.delivery_order?.id || idx"
            :class="!isSingleComplete ? 'card-body card-body-collapse p-2 mb-2' : 'mb-2'"
          >
            <div
              v-if="!isSingleComplete"
              v-b-toggle="'accordion-detail-' + (act?.delivery_order?.id || idx)"
              class="d-flex justify-content-between"
            >
              <div class="card d-flex flex-column gap-1 m-0">
                <div class="d-flex align-items-center gap-2">
                  <div class="d-flex flex-column">
                    <div class="d-flex align-items-center gap-2">
                      <h5 class="mb-0">
                        {{ act?.delivery_order?.number || '-' }}
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
              <div class="d-flex align-items-center">
                <div>
                  <i
                    v-if="getCollapseVisible(act?.delivery_order?.id || idx)"
                    class="mc-fa icon-fill_arrow-ios-down"
                  ></i>
                  <i v-else class="mc-fa icon-fill_arrow-ios-up"></i>
                </div>
              </div>
            </div>
            <b-collapse
              :id="'accordion-detail-' + (act?.delivery_order?.id || idx)"
              :visible="getCollapseVisible(act?.delivery_order?.id || idx)"
              :class="{ 'mt-2': !isSingleComplete }"
              @shown="onCollapseShown(act?.delivery_order?.id || idx)"
              @hidden="onCollapseHidden(act?.delivery_order?.id || idx)"
            >
              <div v-if="isProductTypeLiquid(act?.delivery_order)" class="conversion-card p-2 mt-2">
                <p><strong>{{ $t('fleetTask.deliveryLoads.conversion.title') }}</strong></p>
                <span v-if="quantityConversionError" class="error-message">
                  {{ quantityConversionError }}
                </span>
                <div class="d-flex">
                  <b-col md="4">
                    <FormulateInput
                      :key="act?.delivery_order?.id + '-initialQuantity'"
                      :ref="`${act?.delivery_order?.id}-initialQuantity-ref`"
                      type="text"
                      name="initial-quantity"
                      :placeholder="$t('fleetTask.deliveryLoads.conversion.placeholder.initialQuantity')"
                      :value="getConversionFieldValue(act?.delivery_order?.id, 'initialQuantity')"
                      validation="required"
                      :validation-behavior="
                        getConversionValidationBehavior(act?.delivery_order?.id)
                      "
                      :error-behavior="getConversionValidationBehavior(act?.delivery_order?.id)"
                      :validation-messages="{
                        required: $t('fleetTask.deliveryLoads.conversion.validation.initialQuantityRequired'),
                      }"
                      @input="
                        (val) =>
                          updateConversionField(act?.delivery_order?.id, 'initialQuantity', val)
                      "
                    />
                  </b-col>
                  <b-col md="1" class="w-auto">
                    <div class="dash-line"></div>
                  </b-col>
                  <b-col md="4">
                    <FormulateInput
                      :key="act?.delivery_order?.id + '-finalQuantity'"
                      :ref="`${act?.delivery_order?.id}-finalQuantity-ref`"
                      type="text"
                      name="final-quantity"
                      :placeholder="$t('fleetTask.deliveryLoads.conversion.placeholder.finalQuantity')"
                      :value="getConversionFieldValue(act?.delivery_order?.id, 'finalQuantity')"
                      validation="required"
                      :validation-behavior="
                        getConversionValidationBehavior(act?.delivery_order?.id)
                      "
                      :error-behavior="getConversionValidationBehavior(act?.delivery_order?.id)"
                      :validation-messages="{
                        required: $t('fleetTask.deliveryLoads.conversion.validation.finalQuantityRequired'),
                      }"
                      @input="
                        (val) =>
                          updateConversionField(act?.delivery_order?.id, 'finalQuantity', val)
                      "
                    />
                  </b-col>
                  <b-col md="2">
                    <FormulateInput
                      :key="act?.delivery_order?.id + '-unitConversion'"
                      :ref="`${act?.delivery_order?.id}-unitConversion-ref`"
                      type="select-resourceful"
                      name="unit-conversion"
                      :placeholder="$t('fleetTask.deliveryLoads.conversion.placeholder.unit')"
                      trackBy="id"
                      :optionLabelFn="({ item }) => item.name"
                      :optionsGetter="getOptionUnit"
                      :value="getConversionFieldValue(act?.delivery_order?.id, 'unitConversion')"
                      validation="required"
                      :validation-behavior="
                        getConversionValidationBehavior(act?.delivery_order?.id)
                      "
                      :error-behavior="getConversionValidationBehavior(act?.delivery_order?.id)"
                      :validation-messages="{
                        required: $t('fleetTask.deliveryLoads.conversion.validation.unitRequired'),
                      }"
                      @input="
                        (val) =>
                          updateConversionField(act?.delivery_order?.id, 'unitConversion', val)
                      "
                    />
                  </b-col>
                  <b-col md="2">
                    <McButton
                      variant="primary"
                      :text="$t('fleetTask.deliveryLoads.conversion.action.convert')"
                      class="mt-1"
                      :disabled="isConversionButtonDisabled(act?.delivery_order?.id)"
                      @click="handleToConversion(act)"
                    />
                  </b-col>
                </div>
              </div>
              <ValueHelpTable
                :ref="'vh-' + (act?.delivery_order?.id || idx)"
                class="delivery-loads-table"
                :isBusy.sync="isLoadingMap[act?.delivery_order?.id]"
                :attrs="tableAttrs"
                :listGetter="listGetterFor(act?.delivery_order?.id)"
                :localItems="resolveTableLocalItems(act?.delivery_order?.id)"
                :multiple="false"
                :selectable="false"
                :totalCount.sync="totalCountMap[act?.delivery_order?.id]"
                :showCaption="true"
              >
                <template #product_name="{ item }">
                  <div class="cell-display">
                    <div class="primary-display">
                      {{ item.product.name }}
                    </div>
                    <div class="secondary-display">
                      {{ item.product.product_type.name }}
                    </div>
                  </div>
                </template>
                <template #quantity="{ item }">
                  <div class="cell-display">
                    <div class="primary-display">{{ formatNumber(item.quantity) }} {{ item.uom }}</div>
                    <div class="secondary-display">
                      {{ formatNumber(item.total_weight) }} {{ $t('fleetTask.deliveryLoads.units.kg') }}
                      <span class="dot-separator">•</span>
                      {{ formatNumber(item.total_volume) }} {{ $t('fleetTask.deliveryLoads.units.cbm') }}
                    </div>
                  </div>
                </template>
                <template #actual="{ item }">
                  <div class="cell-display">
                    <div class="primary-display d-flex align-items-center">
                      <div class="flex-grow-1">
                        <FormulateInput
                          v-if="
                            isPickUp &&
                            !item.quantity_delivered &&
                            item.quantity_picked !== 0 &&
                            item.quantity_delivered !== 0
                          "
                          :key="item.id + '-actual'"
                          :ref="`${item.id}-actual-ref`"
                          type="text"
                          :validation="validationRuleActual(item)"
                          :validation-messages="getActualValidationMessages(item)"
                          error-behavior="blur"
                          :class="{
                            'yellow-border':
                              modifiedQuantities[`actual-${item.id}`] &&
                              !(
                                fieldErrors[`actual-${item.id}`] &&
                                fieldErrors[`actual-${item.id}`].hasErrors
                              ) &&
                              modifiedQuantities[`actual-${item.id}`] != (item.quantity_picked ?? item.quantity),
                          }"
                          :value="
                            modifiedQuantities[`actual-${item.id}`] !== undefined
                              ? modifiedQuantities[`actual-${item.id}`]
                              : (item.quantity_picked ?? (!isProductTypeLiquid(act?.delivery_order) ? item.quantity : null))
                          "
                          @input="(val) => handleActualChange(item, val)"
                          @validation="($event) => handleValidation(`actual-${item.id}`, $event)"
                        >
                          <template #suffix>
                            <div class="formulate-input-suffix">
                              <span>{{ item.uom }}</span>
                            </div>
                          </template>
                        </FormulateInput>
                        <div v-else>{{ formatNumber(item.quantity_picked) }} {{ item.uom }}</div>
                      </div>
                      <i
                        v-if="
                          item.is_adjusted_picked &&
                          isPickUp &&
                          !item.quantity_delivered &&
                          item.quantity_picked !== 0 &&
                          item.quantity_delivered !== 0
                        "
                        :id="`tooltip-actual-${item.id}`"
                        class="mc-fa icon-outline_info ml-2"
                      ></i>
                      <b-tooltip
                        :target="`tooltip-actual-${item.id}`"
                        triggers="hover"
                        placement="bottom"
                      >
                        {{ $t('fleetTask.deliveryLoads.tooltip.lastUpdatedChange') }}
                        <strong> {{ formatNumber(item.quantity_picked) }} {{ item.uom }} </strong>
                      </b-tooltip>
                    </div>
                    <div
                      class="secondary-display"
                      :class="{
                        'modified-value':
                          modifiedQuantities[`actual-${item.id}`] &&
                          modifiedQuantities[`actual-${item.id}`] != (item.quantity_picked ?? item.quantity),
                      }"
                    >
                      {{
                        formatNumber(
                          getUpdatedWeight(
                            item,
                            modifiedQuantities[`actual-${item.id}`],
                            'pick'
                          )
                        )
                      }}
                      {{ $t('fleetTask.deliveryLoads.units.kg') }}
                      <span class="dot-separator">•</span>
                      {{
                        formatNumber(
                          getUpdatedVolume(
                            item,
                            modifiedQuantities[`actual-${item.id}`],
                            'pick'
                          )
                        )
                      }}
                      {{ $t('fleetTask.deliveryLoads.units.cbm') }}
                    </div>
                  </div>
                </template>
                <template #drop="{ item }">
                  <div class="cell-display">
                    <div class="primary-display d-flex align-items-center">
                      <div class="flex-grow-1">
                        <FormulateInput
                          v-if="shouldShowDropInput(item)"
                          :key="item.id + '-drop'"
                          :ref="`${item.id}-drop-ref`"
                          v-model="modifiedQuantities[`drop-${item.id}`]"
                          type="text"
                          :validation="validationRuleDrop(item)"
                          :validation-messages="getDropValidationMessages(item)"
                          error-behavior="blur"
                          :show-errors="Boolean(conversionSubmitAttemptedMap[activity?.delivery_order?.id])"
                          :class="{
                            'yellow-border':
                              modifiedQuantities[`drop-${item.id}`] &&
                              !(
                                fieldErrors[`drop-${item.id}`] &&
                                fieldErrors[`drop-${item.id}`].hasErrors
                              ) &&
                              modifiedQuantities[`drop-${item.id}`] != (item.quantity_delivered ?? (item.quantity_picked ?? item.quantity)),
                          }"
                          @input="(val) => handleDropChange(item, val)"
                          @validation="($event) => handleValidation(`drop-${item.id}`, $event)"
                        >
                          <template #suffix>
                            <div class="formulate-input-suffix">
                              <span>{{ item.uom }}</span>
                            </div>
                          </template>
                        </FormulateInput>
                        <div v-else>
                          {{
                            formatNumber(
                              item.quantity_delivered === 0
                              ? item.quantity_delivered
                              : item.quantity_picked
                            )
                          }}
                          {{ item.uom }}
                        </div>
                      </div>
                      <i
                        v-if="
                          item.is_adjusted_delivered &&
                          !isPickUp &&
                          item.quantity_delivered > 0 &&
                          item.quantity_picked > 0
                        "
                        :id="`tooltip-drop-${item.id}`"
                        class="mc-fa icon-outline_info ml-2"
                      ></i>
                      <b-tooltip
                        :target="`tooltip-drop-${item.id}`"
                        triggers="hover"
                        placement="bottom"
                      >
                        {{ $t('fleetTask.deliveryLoads.tooltip.lastUpdatedChange') }}
                        <strong> {{ formatNumber(item.quantity_delivered) }} {{ item.uom }} </strong>
                      </b-tooltip>
                    </div>
                    <div
                      class="secondary-display"
                      :class="{
                        'modified-value':
                          modifiedQuantities[`drop-${item.id}`] &&
                          modifiedQuantities[`drop-${item.id}`] != (item.quantity_delivered ?? (item.quantity_picked ?? item.quantity)),
                      }"
                    >
                      {{
                        formatNumber(
                          getUpdatedWeight(
                            item,
                            modifiedQuantities[`drop-${item.id}`],
                            isProductTypeLiquid(act?.delivery_order)?
                            'drop-conversion':'drop'
                          )
                        )
                      }}
                      {{ $t('fleetTask.deliveryLoads.units.kg') }}
                      <span class="dot-separator">•</span>
                      {{
                        formatNumber(
                          getUpdatedVolume(
                            item,
                            modifiedQuantities[`drop-${item.id}`],
                            isProductTypeLiquid(act?.delivery_order)?
                            'drop-conversion':'drop'
                          )
                        )
                      }}
                      {{ $t('fleetTask.deliveryLoads.units.cbm') }}
                    </div>
                    <span v-if="getConversionDropError(item)" class="error-message">
                      {{ getConversionDropError(item) }}
                    </span>
                  </div>
                </template>
                <template #reason="{ item }">
                  <div class="cell-display">
                    <div class="primary-display">
                      <div
                        v-if="
                          isSamator && isLiquidLoadItem(item) ? 
                          (isPickUp && isCompletedPickUp && isCompletedDrop)
                          :
                          (
                            (isPickUp &&
                              (item.quantity_delivered === 0 || item.quantity_picked === 0)) ||
                            (isPickUp && isCompletedPickUp && isCompletedDrop) ||
                            (!isPickUp &&
                              (item.quantity_picked === 0 || item.quantity_delivered === 0))
                          )
                        "
                      >
                        {{ ((isPickUp ? item.adjustment_reason_picked : item.adjustment_reason_delivered) || '').trim() || '-' }}
                      </div>
                      <div v-else class="custom-form-group">
                        <FormulateInput
                          :key="item.id + '-reason'"
                          :ref="`${item.id}-reason-ref`"
                          :value="
                            modifiedQuantities[`reason-${item.id}`] !== undefined
                              ? modifiedQuantities[`reason-${item.id}`]
                              : item.reason
                          "
                          :placeholder="$t('fleetTask.deliveryLoads.reason.placeholder')"
                          type="text"
                          :disabled="isProductTypeLiquid(act?.delivery_order) ?
                            !localIsAdjustedActivity 
                            : reasonDisabledMap[item.id] !== false
                          "
                          :validation="shouldRequireReason(item) ? 'required' : ''"
                          :validation-messages="{
                            required: $t('fleetTask.deliveryLoads.reason.validation.required'),
                          }"
                          error-behavior="submit"
                          :class="{ 'error-input': errorFields.includes(item.id) }"
                          @input="(val) => updateReason(item, val)"
                          @validation="($event) => handleValidation(`reason-${item.id}`, $event)"
                        />
                        <span v-if="errorFields.includes(item.id)" class="error-message">
                          {{ $t('fleetTask.deliveryLoads.reason.validation.required') }}
                        </span>
                      </div>
                    </div>
                  </div>
                </template>
              </ValueHelpTable>
            </b-collapse>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import McBadge from '@/components/McBadge'
import McButton from '@/components/McButton'
import ValueHelpTable from '@/components/formulate/ValueHelpInputV2/components/ModalBody/Table'
import * as Load from '@/services/rest-api/lib/delivery/load'
import { quantityConversion } from '@/services/rest-api/lib/fleet-task/samator'
import { formatDateWithTz } from '@/helpers/tzDate'
import * as historyApi from '@/services/rest-api/lib/delivery/history'
import { startInitialDeliveryLoadPrefetch } from '../lib/delivery-load-step'
import moment from 'moment'
import { formatNumber } from '@/helpers/format-number.js'
import { Helper } from '@/mixin/helper'

moment.locale('id')

export default {
  name: 'DeliveryLoad',
  components: {
    McBadge,
    ValueHelpTable,
    McButton,
  },
  mixins: [Helper],
  props: {
    fleetTaskId: {
      type: String,
      default: '',
    },
    customerNotes: {
      type: [Array, String, Number, Object],
      default: null,
    },
    customerNotesThirdPartyId: {
      type: String,
      default: null,
    },
    activity: {
      type: Object,
      default: () => ({}),
    },
    isPickUp: {
      type: Boolean,
      default: true,
    },
    isComplete: {
      type: Boolean,
      default: false,
    },
    isCompletedPickUp: {
      type: Boolean,
      default: false,
    },
    isCompletedDrop: {
      type: Boolean,
      default: false,
    },
    isAdjustedActivity: {
      type: Boolean,
      default: false,
    },
    activities: {
      type: Array,
      default: () => [],
    },
    isSingleComplete: {
      type: Boolean,
      default: false,
    },
    selectedActivity: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      quantityConversionError: null,
      isLoading: false,
      isLoadingMap: {},
      dataLoaded: false,
      totalCount: 0,
      totalCountMap: {},
      isAdjustedPicked: false,
      isAdjustedDelivered: false,
      reasonDisabledMap: {},
      isFromDetail: false,
      modifiedQuantities: {},
      adjustedDeliveryLoads: [],
      lastUpdateFormatted: '-',
      errorFields: [],
      validationErrors: {},
      fieldErrors: {},
      searchQuery: '',
      loadsCache: {},
      fullLoadsCache: {},
      collapseState: {},
      isInitialPrefetching: false,
      initialPrefetchPromise: null,
      conversionForms: {},
      conversionSubmitAttemptedMap: {},
      conversionLoadingMap: {},
      conversionDropErrors: {},
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isSamator() {
      const data = this.getFeatureFlagValue('customFeature.samator')
      return (
        Array.isArray(data?.company_id) && 
        data.company_id.includes(this.getCompanyId())
      )
    },
    baseTableFields() {
      return [
        {
          key: 'product.name',
          label: this.$t('fleetTask.deliveryLoads.table.productName'),
          isSortable: false,
          isSlot: true,
          name: 'product_name',
        },
        {
          key: 'quantity',
          label: this.$t('fleetTask.deliveryLoads.table.plan'),
          isSortable: false,
          isSlot: true,
          name: 'quantity',
        },
        {
          key: 'actual_pickup',
          label: this.$t('fleetTask.deliveryLoads.table.actualPickup'),
          isSortable: false,
          isSlot: true,
          name: 'actual',
        },
        {
          key: 'reason',
          label: this.$t('fleetTask.deliveryLoads.table.changeReason'),
          isSortable: false,
          isSlot: true,
          name: 'reason',
        },
      ]
    },
    baseActivities() {
      if (this.isSingleComplete && this.selectedActivity?.delivery_order?.id) {
        return [this.selectedActivity]
      }

      if (!Array.isArray(this.activities)) return []

      // Selalu filter aktivitas yang tidak memiliki load (load_count > 0) dan bukan dari vendor request
      return this.activities.filter((act) => {
        const doData = act?.delivery_order
        return (doData?.load_count || 0) > 0 && !doData?.is_from_vendor_request
      })
    },
    filteredActivities() {
      const activities = this.baseActivities

      const rawQuery = this.searchQuery || ''
      const query = rawQuery.trim().toLowerCase()
      if (!query) return activities

      return activities.filter((act) => {
        const doNumber = act?.delivery_order?.number?.toLowerCase() || ''
        if (doNumber.includes(query)) return true

        const loads = this.fullLoadsCache[act?.delivery_order?.id] || []
        const hasLoadMatch = loads.some((load) => {
          const prodName = load?.product?.name?.toLowerCase() || ''
          const prodType = load?.product?.product_type?.name?.toLowerCase() || ''
          return prodName.includes(query) || prodType.includes(query)
        })
        return hasLoadMatch
      })
    },
    localFilteredLoads() {
      const filtered = {}
      const rawQuery = this.searchQuery || ''
      const query = rawQuery.trim().toLowerCase()

      this.baseActivities.forEach((act) => {
        const id = act?.delivery_order?.id
        const loads = this.fullLoadsCache[id] || this.loadsCache[id] || []

        if (!query) {
          filtered[id] = loads
          return
        }

        // Jika nomor DO cocok, tampilkan semua produk di dalam DO tersebut
        const doNumber = act?.delivery_order?.number?.toLowerCase() || ''
        if (doNumber.includes(query)) {
          filtered[id] = loads
          return
        }

        // Jika nomor DO tidak cocok, filter produknya
        filtered[id] = loads.filter((load) => {
          const prodName = load?.product?.name?.toLowerCase() || ''
          const prodType = load?.product?.product_type?.name?.toLowerCase() || ''
          return prodName.includes(query) || prodType.includes(query)
        })
      })
      return filtered
    },
    showSearchEmptyState() {
      const rawQuery = this.searchQuery || ''
      const hasActiveSearch = rawQuery.trim().length > 0
      return !this.isSingleComplete && hasActiveSearch && this.filteredActivities.length === 0
    },
    tableAttrs() {
      const tableFields = [...this.baseTableFields]
      if (!this.isPickUp) {
        const dropField = {
          key: 'actual_drop',
          label: this.$t('fleetTask.deliveryLoads.table.actualDelivery'),
          isSortable: false,
          isSlot: true,
          name: 'drop',
        }
        const reasonIndex = tableFields.findIndex((field) => field.key === 'reason')
        if (reasonIndex !== -1) {
          tableFields.splice(reasonIndex, 0, dropField)
        } else {
          tableFields.push(dropField)
        }
      }

      return {
        name: 'delivery-load',
        trackBy: 'id',
        tableFields: tableFields,
        hasSearch: false,
      }
    },
    badgeIcon() {
      return this.localIsAdjustedActivity
        ? 'icon-fill_alert-triangle'
        : 'icon-fill_checkmark-circle'
    },
    badgeVariant() {
      return this.localIsAdjustedActivity ? 'warning' : 'success'
    },
    badgeText() {
      return this.localIsAdjustedActivity
        ? this.$t('fleetTask.deliveryLoads.badge.quantityChanged')
        : this.$t('fleetTask.deliveryLoads.badge.asPlanned')
    },
    badgeOutline() {
      return this.localIsAdjustedActivity ? 'outlineWarning' : 'outlineSuccess'
    },
    localIsAdjustedActivity() {
      const hasLocalAdjustments =
        this.adjustedDeliveryLoads && this.adjustedDeliveryLoads.length > 0

      return (
        this.isAdjustedActivity ||
        hasLocalAdjustments ||
        this.isAdjustedPicked ||
        this.isAdjustedDelivered
      )
    },
    getUpdatedWeight() {
      return this.getUpdatedValue('weight')
    },
    getUpdatedVolume() {
      return this.getUpdatedValue('volume')
    },
  },
  watch: {
    searchQuery(newVal) {
      const raw = newVal || ''
      const query = raw.trim().toLowerCase()

      if (!query) {
        return
      }

      this.filteredActivities.forEach((act) => {
        const id = act?.delivery_order?.id
        if (id) {
          this.$set(this.collapseState, id, true)
        }
      })
    },
    isComplete: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.dataLoaded = false
        }
      },
    },
  },
  mounted() {
    if (this.activity?.delivery_order?.id && this.isAdjustedActivity) {
      this.fetchLastUpdate()
    }
  },
  methods: {
    async getOptionUnit(opts = {}) {
      const data = this.getFeatureFlagValue('customFeature.samator')

      let units = []
      if (this.isSamator) {
        const liquidConversion = data?.liquid_conversion || []
        units = liquidConversion.map((item) => ({
          id: item.toLowerCase(),
          name: item,
        }))
      }

      const search = (opts?.search || '').toString().trim().toLowerCase()
      const filteredUnits = search
        ? units.filter((item) => item.name.toLowerCase().includes(search))
        : units

      return {
        isPagination: false,
        metadata: {
          total_page: 1,
          total_count: filteredUnits.length,
          page: 1,
        },
        data: {
          paginated_result: filteredUnits,
        },
      }
    },
    formatDateWithTz,
    getActualValidationMessages(item) {
      return {
        required: this.$t('fleetTask.deliveryLoads.validation.quantityRequired'),
        number: this.$t('fleetTask.deliveryLoads.validation.mustBeNumber'),
        decimal: this.$t('fleetTask.deliveryLoads.validation.maxThreeDecimals'),
        min: this.$t('fleetTask.deliveryLoads.validation.minZero'),
        max: item.is_adjusted_picked
          ? this.$t('fleetTask.deliveryLoads.validation.actualExceedsLastChange')
          : this.$t('fleetTask.deliveryLoads.validation.actualExceedsPlan'),
      }
    },
    getDropValidationMessages(item) {
      return {
        required: this.$t('fleetTask.deliveryLoads.validation.quantityRequired'),
        number: this.$t('fleetTask.deliveryLoads.validation.mustBeNumber'),
        decimal: this.$t('fleetTask.deliveryLoads.validation.maxThreeDecimals'),
        min: this.$t('fleetTask.deliveryLoads.validation.minZero'),
        max:
          item.is_adjusted_picked && item.is_adjusted_delivered
            ? this.$t('fleetTask.deliveryLoads.validation.deliveredActualExceedsLastChange')
            : item.is_adjusted_picked
            ? this.$t('fleetTask.deliveryLoads.validation.deliveredActualExceedsPickupActual')
            : item.is_adjusted_delivered
            ? this.$t('fleetTask.deliveryLoads.validation.deliveredActualExceedsLastChange')
            : this.$t('fleetTask.deliveryLoads.validation.deliveredActualExceedsPlan'),
      }
    },
    getConversionForm(deliveryOrderId) {
      if (!deliveryOrderId) {
        return {
          initialQuantity: '',
          finalQuantity: '',
          unitConversion: null,
        }
      }

      if (!Object.prototype.hasOwnProperty.call(this.conversionForms, deliveryOrderId)) {
        this.$set(this.conversionForms, deliveryOrderId, {
          initialQuantity: '',
          finalQuantity: '',
          unitConversion: null,
        })
      }

      return this.conversionForms[deliveryOrderId]
    },
    getConversionFieldValue(deliveryOrderId, fieldName) {
      return this.getConversionForm(deliveryOrderId)[fieldName]
    },
    isFilledConversionValue(value) {
      if (value === null || value === undefined) return false
      if (typeof value === 'string') return value.trim() !== ''
      if (typeof value === 'object') {
        return Boolean(value.id || value.value || value.name)
      }
      return true
    },
    isConversionFormComplete(deliveryOrderId) {
      const form = this.getConversionForm(deliveryOrderId)

      return (
        this.isFilledConversionValue(form.initialQuantity) &&
        this.isFilledConversionValue(form.finalQuantity) &&
        this.isFilledConversionValue(form.unitConversion)
      )
    },
    getConversionValidationBehavior(deliveryOrderId) {
      return this.conversionSubmitAttemptedMap[deliveryOrderId] ? 'live' : 'submit'
    },
    updateConversionField(deliveryOrderId, fieldName, value) {
      const form = this.getConversionForm(deliveryOrderId)

      this.$set(form, fieldName, value)
      this.clearConversionDropError(deliveryOrderId)
    },
    isConversionButtonDisabled(deliveryOrderId) {
      return (
        !this.isConversionFormComplete(deliveryOrderId) ||
        this.conversionLoadingMap[deliveryOrderId]
      )
    },
    parseConversionNumber(value) {
      if (value === null || value === undefined) return NaN
      const normalized = value.toString().replace(',', '.').trim()
      return Number(normalized)
    },
    getCustomerNoteThirdPartyId() {
      const notes = this.customerNotes

      if (Array.isArray(notes)) {
        return notes.find((value) => value !== null && value !== undefined && value !== '') || ''
      }

      if (notes && typeof notes === 'object') {
        return notes.id || notes.uuid || notes.third_party_id || notes.value || ''
      }

      return notes || ''
    },
    getConversionUom(unitConversion) {
      if (!unitConversion) return ''
      if (typeof unitConversion === 'string') return unitConversion
      return unitConversion.name || unitConversion.id || ''
    },
    isPoDAdjustmentPhase() {
      return !this.isPickUp && this.isCompletedPickUp && !this.isCompletedDrop
    },
    isLiquidLoadItem(item = {}) {
      if (typeof item?.__isProductTypeLiquid === 'boolean') {
        return item.__isProductTypeLiquid
      }

      const deliveryOrderId = this.getDeliveryOrderIdFromLoad(item)
      if (!deliveryOrderId) return false

      const matchedActivity = this.baseActivities.find(
        (act) => act?.delivery_order?.id === deliveryOrderId
      )

      return this.isProductTypeLiquid(matchedActivity?.delivery_order)
    },
    shouldShowDropInput(item) {
      if (this.isPickUp) return false
      if (this.modifiedQuantities[`drop-${item.id}`] !== undefined) return true
      return item.quantity_picked !== 0 || item.quantity_delivered === null
    },
    getDeliveryOrderIdFromLoad(item = {}) {
      if (item.__deliveryOrderId) return item.__deliveryOrderId

      const idFromLoads = Object.keys(this.loadsCache).find((doId) =>
        (this.loadsCache[doId] || []).some((load) => load.id === item.id)
      )
      if (idFromLoads) return idFromLoads

      const idFromFullLoads = Object.keys(this.fullLoadsCache).find((doId) =>
        (this.fullLoadsCache[doId] || []).some((load) => load.id === item.id)
      )
      if (idFromFullLoads) return idFromFullLoads

      return this.selectedActivity?.delivery_order?.id || null
    },
    isDropInputDisabled(item) {
      return this.isLiquidLoadItem(item)
    },
    async applyConvertedDropQuantity(deliveryOrderId, qty) {
      if (this.isPickUp) return

      this.clearConversionDropError(deliveryOrderId)

      let loads = this.loadsCache[deliveryOrderId] || this.fullLoadsCache[deliveryOrderId] || []
      if (!loads.length) {
        const response = await this.getDeliveryLoadDataById(deliveryOrderId, {})
        loads = response?.data?.paginated_result || []
      }

      const loadItem = loads.find((item) => item?.quantity_picked !== 0) || loads[0]

      if (!loadItem) return

      const convertedQty = Number(qty)
      this.$set(loadItem, 'quantity_delivered', convertedQty)
      ;[this.loadsCache[deliveryOrderId], this.fullLoadsCache[deliveryOrderId]]
        .filter((list) => Array.isArray(list))
        .forEach((list) => {
          const target = list.find((item) => item.id === loadItem.id)
          if (target) {
            this.$set(target, 'quantity_delivered', convertedQty)
          }
        })

      this.handleDropChange(loadItem, String(qty))
    },
    async handleToConversion(activity) {
      const deliveryOrderId = activity?.delivery_order?.id
      if (!deliveryOrderId) return

      this.$set(this.conversionSubmitAttemptedMap, deliveryOrderId, true)

      if (!this.isConversionFormComplete(deliveryOrderId)) {
        return
      }

      const form = this.getConversionForm(deliveryOrderId)
      const payload = {
        fleet_task_id: this.fleetTaskId || '',
        order_id: deliveryOrderId,
        customer_note_third_party_id: this.customerNotesThirdPartyId,
        start_qty: this.parseConversionNumber(form.initialQuantity),
        end_qty: this.parseConversionNumber(form.finalQuantity),
        uom: this.getConversionUom(form.unitConversion),
      }

      if (isNaN(payload.start_qty) || isNaN(payload.end_qty) || !payload.uom) {
        return
      }

      this.$set(this.conversionLoadingMap, deliveryOrderId, true)

      try {
        const response = await quantityConversion(payload)
        if (response.code !== 200) {
          this.quantityConversionError = response.message || this.$t('global.somethingWentWrong')
          return
        }
        this.quantityConversionError = null
        
        const qty = response?.data?.qty

        if (typeof qty === 'number' && !isNaN(qty)) {
          let loads = this.loadsCache[deliveryOrderId] || this.fullLoadsCache[deliveryOrderId] || []
          if (!loads.length) {
            const loadResponse = await this.getDeliveryLoadDataById(deliveryOrderId, {})
            loads = loadResponse?.data?.paginated_result || []
          }

          const loadItem = loads.find((item) => item?.quantity_picked !== 0) || loads[0]
          const pickupQty = Number(loadItem?.quantity_picked)

          const isLiquidLoad = this.isLiquidLoadItem(loadItem)
          if (!isLiquidLoad && loadItem && !isNaN(pickupQty) && qty > pickupQty) {
            this.clearConversionDropError(deliveryOrderId)
            this.$set(this.conversionDropErrors, loadItem.id, {
              deliveryOrderId,
              message: this.$t('fleetTask.deliveryLoads.validation.deliveredActualExceedsPickupActual'),
            })
            this.$set(this.fieldErrors, `conversion-${deliveryOrderId}`, { hasErrors: true })
            this.checkGlobalValidation()
            return
          }
          
          this.clearConversionDropError(deliveryOrderId)
          await this.applyConvertedDropQuantity(deliveryOrderId, qty)
        }
      } catch (error) {
        this.quantityConversionError =
          error?.response?.data?.message ||
          error?.message ||
          this.$t('global.somethingWentWrong')
      } finally {
        this.$set(this.conversionLoadingMap, deliveryOrderId, false)
      }
    },
    formatNumber(value) {
      return formatNumber(value, 3) || value
    },
    validationRuleActual(item) {
      const maxLimit = item.is_adjusted_picked ? item.quantity_picked : item.quantity
      return `bail|number|decimal:3|max:${maxLimit}|min:0,value|required`
    },
    validationRuleDrop(item) {
      if (this.isLiquidLoadItem(item)) {
        return 'bail|number|min:0,value'
      }

      const maxLimit =
        item.is_adjusted_picked && item.is_adjusted_delivered
          ? item.quantity_delivered
          : item.is_adjusted_picked
          ? item.quantity_picked
          : item.is_adjusted_delivered
          ? item.quantity_delivered
          : item.quantity
      return `bail|number|decimal:3|max:${maxLimit}|min:0,value|required`
    },
    resolveTableLocalItems(id) {
      return this.getTableLocalItems(this.isSingleComplete, this.localFilteredLoads, id)
    },
    getTableLocalItems(isSingleComplete, localFilteredLoads, activityId) {
      if (isSingleComplete) {
        return null
      }

      return localFilteredLoads?.[activityId] || []
    },
    listGetterFor(id) {
      return (opts) => this.getDeliveryLoadDataById(id, opts)
    },
    getCollapseVisible(id) {
      if (this.collapseState[id] !== undefined) {
        return this.collapseState[id]
      }
      return this.isSingleComplete
    },
    onCollapseShown(id) {
      this.$set(this.collapseState, id, true)
      if (!this.isSingleComplete && !Object.prototype.hasOwnProperty.call(this.loadsCache, id)) {
        this.triggerTableDataFetch(id)
      }
    },
    onCollapseHidden(id) {
      this.$set(this.collapseState, id, false)
    },
    santitizeNumber(num) {
      const number = Number(num)

      if (Number.isInteger(number)) {
        return formatNumber(number, 3)
      }

      return Number(number.toString().replace(/\.0+$|(\.\d*[1-9])0+$/, '$1'))
    },
    async prefetchAllDetailLoads() {
      if (this.isSingleComplete && this.selectedActivity?.delivery_order?.id) {
        const id = this.selectedActivity.delivery_order.id
        await this.getDeliveryLoadDataById(id, {})
        return
      }

      if (!Array.isArray(this.baseActivities)) return
      const ids = [...new Set(this.baseActivities.map((a) => a.delivery_order.id))]
      await Promise.allSettled(ids.map((id) => this.getDeliveryLoadDataById(id, { })))
    },
    resetInitialPrefetchState() {
      this.isInitialPrefetching = false
      this.initialPrefetchPromise = null
      this.dataLoaded = false
      this.searchQuery = ''
      this.loadsCache = {}
      this.fullLoadsCache = {}
      this.totalCountMap = {}
      this.isLoadingMap = {}
      this.collapseState = {}
    },
    getUpdatedValue(field) {
      return (item, inputValue, column) => {
        let newQuantity
        if (
          (!inputValue || inputValue.trim() === '') 
          && column !== 'drop-conversion'
        ) {
          if (item.is_adjusted_picked && item.is_adjusted_delivered) {
            if (this.isCompletedPickUp && this.isPickUp) {
              return item[`${field}_picked`]
            }
            return item[`${field}_delivered`]
          } else if (item.is_adjusted_delivered) {
            return item[`${field}_delivered`]
          } else if (
            item.is_adjusted_picked 
            && !this.isPickUp 
            && `drop-${item.id}` in this.modifiedQuantities
            && column === 'drop'
          ) {
            return '-'
          } else if (item.is_adjusted_picked) {
            return item[`${field}_picked`]
          } else if (column === 'pick') {
            return `actual-${item.id}` in this.modifiedQuantities ? '-' : (item[`${field}_picked`] ?? item[`total_${field}`])
          } else if (column === 'drop') {
            return `drop-${item.id}` in this.modifiedQuantities ? '-' : (item[`${field}_delivered`] ?? (item[`${field}_picked`] ?? item[`total_${field}`]))
          }
          return '-'
        } else{
          if (column === 'drop-conversion'){
            newQuantity = parseFloat(
              this.modifiedQuantities[`drop-${item.id}`] 
              ?? item.quantity_picked
            )
          } else if (column === 'pick' || column === 'drop') {
            newQuantity = parseFloat(inputValue)
          }
        }

        if (isNaN(newQuantity)) {
          return '-'
        }

        const conversionKey = `conversion_${field}_value`
        const result = newQuantity * item[conversionKey]
        return this.santitizeNumber(result)
      }
    },
    async getDeliveryLoadData(opts) {
      const doId = this.isSingleComplete
        ? this.selectedActivity?.delivery_order?.id
        : this.activity?.delivery_order?.id

      if (!doId) {
        return {
          data: { paginated_result: [] },
          metadata: { total_count: 0, page: 1 },
        }
      }

      const act = this.isSingleComplete
        ? this.selectedActivity
        : this.activities.find((a) => a?.delivery_order?.id === doId)

      if (act && (act?.delivery_order?.load_count || 0) <= 0) {
        return {
          data: { paginated_result: [] },
          metadata: { total_count: 0, page: 1 },
        }
      }

      this.isLoading = true

      try {
        const response = await Load.get(doId)(opts || {})
        const isLiquid = this.isProductTypeLiquid(act?.delivery_order)
        const results = (response?.data?.paginated_result || []).map((item) => ({
          ...item,
          __deliveryOrderId: doId,
          __isProductTypeLiquid: isLiquid,
        }))
        this.totalCount = response?.metadata?.total_count || 0

        this.setAdjustmentFlags(results)

        this.isFromDetail = true

        return response
      } catch (error) {
        return {
          data: { paginated_result: [] },
          metadata: { total_count: 0, page: 1 },
        }
      } finally {
        this.isLoading = false
      }
    },
    async getDeliveryLoadDataById(id, opts) {
      const act = this.activities.find((a) => a?.delivery_order?.id === id)
      if (act && (act?.delivery_order?.load_count || 0) <= 0) {
        return {
          data: { paginated_result: [] },
          metadata: { total_count: 0, page: 1 },
        }
      }
      this.$set(this.isLoadingMap, id, true)
      try {
        const response = await Load.get(id)(opts || {})
        const isLiquid = this.isProductTypeLiquid(act?.delivery_order)
        const results = (response?.data?.paginated_result || []).map((item) => ({
          ...item,
          __deliveryOrderId: id,
          __isProductTypeLiquid: isLiquid,
        }))

        if (this.adjustedDeliveryLoads.length) {
          results.forEach((item) => {
            const adjustment = this.adjustedDeliveryLoads.find(
              (a) => a.delivery_load_uuid === item.id
            )
            if (adjustment) {
              const quantity = adjustment.quantity
              if (this.isPickUp) {
                this.$set(this.modifiedQuantities, `actual-${item.id}`, quantity)
                const refValue = item.quantity_picked ?? item.quantity
                const isEqualOrMore = quantity >= refValue
                this.$set(this.reasonDisabledMap, item.id, isEqualOrMore)
              } else {
                this.$set(this.modifiedQuantities, `drop-${item.id}`, quantity)
                const refValue = item.quantity_delivered ?? (item.quantity_picked ?? item.quantity)
                const isEqualOrMore = quantity >= refValue
                const shouldDisableReason = !this.isPoDAdjustmentPhase() || isEqualOrMore
                this.$set(this.reasonDisabledMap, item.id, shouldDisableReason)
              }
              if (adjustment.reason) {
                item.reason = adjustment.reason
                this.$set(this.modifiedQuantities, `reason-${item.id}`, adjustment.reason)
              }
            }
          })
        }

        const count = response?.metadata?.total_count || 0
        this.$set(this.totalCountMap, id, count)
        this.$set(this.loadsCache, id, results)
        if (!opts?.search) {
          this.$set(this.fullLoadsCache, id, results)
        }
        this.setAdjustmentFlags(results)
        this.isFromDetail = true
        return response
      } catch (error) {
        this.$set(this.totalCountMap, id, 0)
        return {
          data: { paginated_result: [] },
          metadata: { total_count: 0, page: 1 },
        }
      } finally {
        this.$set(this.isLoadingMap, id, false)
      }
    },
    setAdjustmentFlags(results) {
      this.isAdjustedPicked = false
      this.isAdjustedDelivered = false

      if (this.isPickUp) {
        this.isAdjustedPicked = results.some((item) => item.is_adjusted_picked === true)
      } else {
        this.isAdjustedDelivered = results.some((item) => item.is_adjusted_delivered === true)
      }
    },
    handleValidation(fieldName, validationObject) {
      let nextFieldErrors
      if(validationObject.hasErrors){
        nextFieldErrors = {
          ...this.fieldErrors,
          [fieldName]: validationObject,
        }
      }else{
        const tmp = { ...this.fieldErrors }
        delete tmp[fieldName]
        nextFieldErrors = tmp
      }
      this.$set(this, 'fieldErrors', nextFieldErrors)
      this.checkGlobalValidation(nextFieldErrors)
    },
    parseFieldKey(key = '') {
      const normalizedKey = String(key)
      const separatorIndex = normalizedKey.indexOf('-')

      if (separatorIndex === -1) {
        return {
          type: normalizedKey,
          itemId: '',
        }
      }

      return {
        type: normalizedKey.slice(0, separatorIndex),
        itemId: normalizedKey.slice(separatorIndex + 1),
      }
    },
    parseRefKey(refKey = '') {
      const normalizedRefKey = String(refKey).replace(/-ref$/, '')
      const separatorIndex = normalizedRefKey.lastIndexOf('-')

      if (separatorIndex === -1) {
        return {
          itemId: normalizedRefKey,
          type: '',
        }
      }

      return {
        itemId: normalizedRefKey.slice(0, separatorIndex),
        type: normalizedRefKey.slice(separatorIndex + 1),
      }
    },
    hasValidationError(refKey) {
      const { itemId, type } = this.parseRefKey(refKey)
      const fieldKey = `${type}-${itemId}`
      const validation = this.fieldErrors[fieldKey]

      if (!validation || !validation.hasErrors) return false

      if (type === 'reason' && this.reasonDisabledMap[itemId] !== false) {
        return false
      }

      if (type === 'drop') {
        const loadItem = this.getLoadItemById(itemId)
        if (loadItem && this.isDropInputDisabled(loadItem)) {
          return false
        }
      }

      return true
    },
    getConversionDropError(item = {}) {
      return this.conversionDropErrors[item.id]?.message || ''
    },
    clearConversionDropError(deliveryOrderId) {
      if (!deliveryOrderId) return

      Object.keys(this.conversionDropErrors).forEach((loadId) => {
        const errorObj = this.conversionDropErrors[loadId]
        if (errorObj?.deliveryOrderId === deliveryOrderId) {
          this.$delete(this.conversionDropErrors, loadId)
        }
      })

      const nextFieldErrors = {
        ...this.fieldErrors,
        [`conversion-${deliveryOrderId}`]: { hasErrors: false },
      }

      this.$set(this, 'fieldErrors', nextFieldErrors)
      this.checkGlobalValidation(nextFieldErrors)
    },
    getLoadItemById(itemId) {
      if (!itemId) return null

      const listGroups = [
        this.tableLocalItems,
        this.localFilteredLoads,
        this.loadsCache,
        this.fullLoadsCache,
      ]

      for (const group of listGroups) {
        if (!group) continue

        if (Array.isArray(group)) {
          const found = group.find((item) => String(item?.id) === String(itemId))
          if (found) return found
          continue
        }

        if (typeof group === 'object') {
          const values = Object.values(group)
          for (const list of values) {
            if (!Array.isArray(list)) continue
            const found = list.find((item) => String(item?.id) === String(itemId))
            if (found) return found
          }
        }
      }

      return null
    },
    handleActualChange(item, value) {
      const isEmpty = !value || value.trim() === ''

      // Ensure both values are numbers for comparison
      const newValue = parseFloat(value)
      const referenceValue = parseFloat(
        item.quantity_picked ?? item.quantity
      )

      let isEqualOrMore = true // Default to disabled
      if (!isNaN(newValue) && !isNaN(referenceValue)) {
        isEqualOrMore = newValue >= referenceValue
      }

      this.$set(this.reasonDisabledMap, item.id, isEmpty || isEqualOrMore)
      this.$set(this.modifiedQuantities, `actual-${item.id}`, value)

      if (!isEmpty && !isEqualOrMore) {
        const numericValue = Number(value)

        const existingIndex = this.adjustedDeliveryLoads.findIndex(
          (load) => load.delivery_load_uuid === item.id
        )

        if (existingIndex !== -1) {
          this.adjustedDeliveryLoads[existingIndex].quantity = numericValue
        } else {
          this.adjustedDeliveryLoads.push({
            delivery_load_uuid: item.id,
            quantity: numericValue,
            reason: null,
          })
        }

        this.$emit('update:adjusted-loads', this.adjustedDeliveryLoads)
      } else {
        const existingIndex = this.adjustedDeliveryLoads.findIndex(
          (load) => load.delivery_load_uuid === item.id
        )

        if (existingIndex !== -1) {
          this.adjustedDeliveryLoads.splice(existingIndex, 1)
          this.$emit('update:adjusted-loads', this.adjustedDeliveryLoads)
        }
      }

      const hasModifiedValues = Object.keys(this.reasonDisabledMap).some(
        (productId) => this.reasonDisabledMap[productId] === false
      )

      if (this.isPickUp) {
        this.isAdjustedPicked = hasModifiedValues
      } else {
        this.isAdjustedDelivered = hasModifiedValues
      }

      this.$emit('update:is-adjusted-activity', hasModifiedValues)

      this.isFromDetail = false

      this.checkGlobalValidation()
    },
    handleDropChange(item, value) {
      const isEmpty = !value || value.trim() === ''
      const canAdjustOnPoD = this.isPoDAdjustmentPhase()

      // Ensure both values are numbers for comparison
      const newValue = parseFloat(value)
      const referenceValue = parseFloat(
        item.quantity_delivered ?? (item.quantity_picked ?? item.quantity)
      )

      let isEqualOrMore = true // Default to disabled
      if (!isNaN(newValue) && !isNaN(referenceValue)) {
        isEqualOrMore = newValue >= referenceValue
      }

      const samatorLiquid = this.isSamator && this.isLiquidLoadItem(item)

      const samatorLiquidModified = 
        !isNaN(newValue)
        && samatorLiquid

      const shouldDisableReason = !canAdjustOnPoD || isEmpty || isEqualOrMore
      this.$set(this.reasonDisabledMap, item.id, shouldDisableReason)
      this.$set(this.modifiedQuantities, `drop-${item.id}`, value)
      
      // input valid angka dan adjusted valid:
      // - samator-liquid (tetap emit converted=true agar validasi di parent bisa jalan):
      //    - nilai sama dengan reference, hapus dari adjusted
      //    - nilai sama dengan reference, tambahkan ke adjusted
      // - non-samator-liquid, validasi = lebih kecil dari reference
      if (canAdjustOnPoD && !isEmpty && (
          samatorLiquidModified || (!samatorLiquid && !isEqualOrMore)
        )
      )
      {
        this.$emit('update:converted', item.id, true)

        if (samatorLiquidModified && (newValue === item.quantity_picked)) {
          const existingIndex = this.adjustedDeliveryLoads.findIndex(
            (load) => load.delivery_load_uuid === item.id
          )
          
          if (existingIndex !== -1) {
            this.adjustedDeliveryLoads.splice(existingIndex, 1)
            this.$emit('update:adjusted-loads', this.adjustedDeliveryLoads)
          }
        } else {
          const numericValue = Number(value)
  
          const existingIndex = this.adjustedDeliveryLoads.findIndex(
            (load) => load.delivery_load_uuid === item.id
          )
  
          if (existingIndex !== -1) {
            this.adjustedDeliveryLoads[existingIndex].quantity = numericValue
          } else {
            this.adjustedDeliveryLoads.push({
              delivery_load_uuid: item.id,
              quantity: numericValue,
              reason: null,
            })
          }
  
          this.$emit('update:adjusted-loads', this.adjustedDeliveryLoads)
        }
      } else {
        const existingIndex = this.adjustedDeliveryLoads.findIndex(
          (load) => load.delivery_load_uuid === item.id
        )
        
        this.$emit('update:converted', item.id, false)
        if (existingIndex !== -1) {
          this.adjustedDeliveryLoads.splice(existingIndex, 1)
          this.$emit('update:adjusted-loads', this.adjustedDeliveryLoads)
        }
      }

      const hasModifiedValues = Object.keys(this.reasonDisabledMap).some(
        (productId) => this.reasonDisabledMap[productId] === false
      )

      if (this.isPickUp) {
        this.isAdjustedPicked = hasModifiedValues
      } else {
        this.isAdjustedDelivered = hasModifiedValues
      }

      this.$emit('update:is-adjusted-activity', hasModifiedValues)

      this.isFromDetail = false

      this.checkGlobalValidation()
    },
    updateReason(item, reason) {
      this.$set(this.modifiedQuantities, `reason-${item.id}`, reason)

      const existingIndex = this.adjustedDeliveryLoads.findIndex(
        (load) => load.delivery_load_uuid === item.id
      )

      if (existingIndex !== -1) {
        this.adjustedDeliveryLoads[existingIndex].reason = reason
        this.$emit('update:adjusted-loads', this.adjustedDeliveryLoads)

        if (reason && this.errorFields.includes(item.id)) {
          const index = this.errorFields.indexOf(item.id)
          if (index !== -1) {
            this.errorFields.splice(index, 1)
          }
        }
      }

      this.checkGlobalValidation()
    },
    checkGlobalValidation(fieldErrors = this.fieldErrors) {
      // Gunakan debounce agar tidak overload
      if (this._validationTimeout) clearTimeout(this._validationTimeout)
      this._validationTimeout = setTimeout(() => {
        // Cek apakah ada field yang memiliki error dan tidak sedang disabled
        const hasErrors = Object.keys(fieldErrors).some((key) => {
          const validation = fieldErrors[key]
          if (!validation || !validation.hasErrors) return false

          // Cek apakah field tersebut sebenarnya disabled
          const { type, itemId } = this.parseFieldKey(key)
          if (type === 'reason' && this.reasonDisabledMap[itemId] !== false) {
            return false
          }

          return true
        })

        this.$emit('update:has-errors', hasErrors)
      }, 100)
    },
    shouldRequireReason(item) {
      // Hanya wajib diisi jika alasan TIDAK disabled
      if (this.reasonDisabledMap[item.id] !== false) {
        return false
      }

      const isAdjusted = item.is_adjusted_picked || item.is_adjusted_delivered
      const isAdjustedPicked = item.is_adjusted_picked
      const isAdjustedDelivered = item.is_adjusted_delivered

      return isAdjusted && (isAdjustedPicked || isAdjustedDelivered)
    },
    async fetchLastUpdate() {
      if (!this.activity?.delivery_order?.id) return

      try {
        const queryParam = {
          page: 1,
          limit: 1,
          sorts: ['created_on desc'],
        }
        const res = await historyApi.getLog(this.activity?.delivery_order?.id, queryParam)

        const data = res.data.paginated_result
        if (data && data.length > 0) {
          const matchingLog = data.find(
            (log) => log.delivery_order_uuid === this.activity?.delivery_order?.id
          )

          if (matchingLog) {
            this.lastUpdateFormatted = this.formatDateWithTz(matchingLog.created_on)
            return
          }
        }

        this.lastUpdateFormatted = '-'
      } catch (error) {
        console.error('Error fetching update history:', error)
        this.lastUpdateFormatted = '-'
      }
    },
    triggerTableDataFetch(id) {
      const refName = `vh-${id}`
      let tableRef = this.$refs[refName]
      if (Array.isArray(tableRef)) {
        tableRef = tableRef[0]
      }
      if (!tableRef || !tableRef.getData) return
      const opts = {
        limit: 10,
        page: 1,
        search: undefined,
        sorts: [],
        filterButton: this.tableAttrs?.filterOptions?.default,
      }
      tableRef.getData(opts, true)
    },
    async emptyListGetter() {
      return {
        data: { paginated_result: [], ids: [] },
        metadata: { total_count: 0, page: 1, total_page: 1, count: 0 },
      }
    },
    getLastUpdate() {
      if (this.lastUpdateFormatted === '-' && this.activity?.delivery_order?.id) {
        this.fetchLastUpdate()
      }
      return this.lastUpdateFormatted
    },
    highlightErrorFields(missingReasons) {
      this.errorFields = []

      missingReasons.forEach((load) => {
        this.errorFields.push(load.delivery_load_uuid)
      })

      this.$forceUpdate()
    },
    isProductTypeLiquid(deliveryOrderDetail = {}) {
      const productTypeField = deliveryOrderDetail?.user_definition_fields?.find(
        (field) => field?.name === 'Product Type'
      )
      const productTypeValue = (productTypeField?.value || '').toString().trim().toLowerCase()

      return ['bulk', 'tube'].includes(productTypeValue)
    },
  },
}
</script>

<style scoped lang="scss">
.mc-fa {
  &:before {
    font-size: 20px;
  }

  &.icon-outline_info {
    &:before {
      font-size: 16px;
    }
  }
}

.icon-container {
  width: 32px;
  height: 32px;
}

.timestamp-text {
  font-size: 12px;
}

.cell-display {
  color: $mep-color-text;

  .primary-display {
    font-size: 13px;
  }

  .secondary-display {
    color: $text-muted;
    font-size: 13px;

    .dot-separator {
      margin: 0 6px;
      font-size: 13px;
      line-height: 1;
      font-weight: 900;
    }
  }
}

.custom-form-group {
  position: relative;
}

.error-input {
  border: 1px solid #dc3545 !important;
  border-radius: 5px;
  padding: 0px;
  :deep(input),
  :deep(.formulate-input-element input) {
    border: none !important;
    border-color: transparent !important;
  }
}

.conversion-card {
  border: 1px solid $teal-100;
  border-radius: 2px;

  .dash-line {
    width: 20px;
    height: 2px;
    background-color: $teal-200;
    border-radius: 999px;
    margin: 20px 10px 0px;
  }
}

.error-message {
  display: block;
  color: #dc3545;
  font-size: 0.7109375rem;
  font-style: italic;
  margin-top: 0px;
}

.yellow-border {
  border: 1px solid $warning !important;
  border-radius: 5px !important;
  padding: 0px;

  :deep(input),
  :deep(.formulate-input-element input) {
    border: none !important;
    border-color: transparent !important;
    border-radius: 5px !important;
  }

  :deep(.formulate-input-element) {
    border-radius: 5px !important;
    &:focus-within {
      border-color: $warning !important;
      box-shadow: 0 0 0 0.2rem rgba(255, 193, 7, 0.25) !important;
      border-radius: 5px !important;
    }
  }

  :deep(input:focus) {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    border-radius: 5px !important;
  }
}

.modified-value {
  color: $warning !important;
  font-weight: 500;
}

.card-body-collapse {
  border: 1px solid $border-color;
  border-radius: 5px !important;
}

:deep(.delivery-loads-table .__table-container) {
  height: auto !important;
  max-height: none !important;
  overflow-y: visible !important;
  border: none;
}
</style>
